import { BreakpointObserver } from '@angular/cdk/layout';
import { AsyncPipe, NgTemplateOutlet } from '@angular/common';
import { Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild, inject } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { environment } from '@ecs-environment';
import { mediaQueries, tracking } from '@ecs-helpers';
import { ModalService } from '@ecs-services/modal/modal.service';
import { SearchCriteriaExplanationService } from '@ecs-services/search/search-help/search-criteria-explanation.service';
import { SearchStoreService } from '@ecs-services/search/search-store.service';
import { SEARCH_TERM_SEPARATOR } from '@ecs-services/search/search.helper';
import { SearchError, SearchResult, SearchResultType } from '@ecs-services/search/search.interface';
import { TranslateDirective, TranslatePipe } from '@ngx-translate/core';
import {
    NamePlateSearchResult,
    NameplateSearchComponent,
    OnDestroyBase,
    SearchHelpComponent,
    SearchHelpCriteria,
    TrackClickDirective,
    TrackingService,
    arrayEqualsShallow,
} from '@os-components';
import { SewButton, SewHeadline, SewInput, SewSearchField } from '@sew/ui-components-angular/standalone';
import { Observable, distinctUntilChanged, filter, map, take, takeUntil } from 'rxjs';
import { endLoading, startLoading } from '../../../../helpers/loading/loading';
import { OrderNumberSearchResult } from '../../../result/spare-parts-result/components/order-number-table/order-number-table.interface';
import { SearchMultipleComponent } from './search-multiple/search-multiple.component';

@Component({
    selector: 'ecs-search-input',
    templateUrl: './search-input.component.html',
    styleUrls: ['./search-input.component.scss'],
    imports: [
        AsyncPipe,
        TranslateDirective,
        NgTemplateOutlet,
        ReactiveFormsModule,
        FormsModule,
        NameplateSearchComponent,
        TrackClickDirective,
        TranslatePipe,
        SewHeadline,
        SewSearchField,
        SewInput,
        SewButton,
        SearchHelpComponent,
    ],
})
export class SearchInputComponent extends OnDestroyBase implements OnInit, OnDestroy {
    private readonly searchStore = inject(SearchStoreService);
    private readonly searchCriteriaExplanationService = inject(SearchCriteriaExplanationService);
    private readonly trackingService = inject(TrackingService);
    private readonly breakpointObserver = inject(BreakpointObserver);
    private readonly modalService = inject(ModalService);

    public searchValue = '';
    public showHelpExpanded = false;
    public isMobile = false;
    public showNameplateMobileIcon = false;
    public error: SearchError | null | undefined = null;
    public ENVIRONMENT = environment;
    public latestSearchTerms$: Observable<string[]>;
    public criteriaExplanation$: Observable<SearchHelpCriteria[]>;

    @ViewChild('searchHelpWrapper') private searchHelpWrapper?: ElementRef;
    @ViewChild('searchfield') private searchfield?: ElementRef;
    @ViewChild('searchButton', { read: ElementRef })
    private searchButtonRef!: ElementRef<HTMLSewButtonElement>;

    public constructor() {
        super();
        this.latestSearchTerms$ = this.searchStore.getSearchHistory();
        this.criteriaExplanation$ = this.searchCriteriaExplanationService.allSearchCriteria;
    }

    @HostListener('body:mousedown', ['$event'])
    public clickHandler($event: MouseEvent) {
        if (
            this.searchfield?.nativeElement !== $event.target &&
            this.searchHelpWrapper?.nativeElement !== $event.target &&
            !this.searchHelpWrapper?.nativeElement.contains($event.target)
        ) {
            this.showHelpExpanded = false;
        }
    }

    public ngOnInit(): void {
        this.searchStore
            .getSearchTerm()
            .pipe(takeUntil(this.destroySubject))
            .subscribe((searchTerms) => {
                this.searchValue = searchTerms.join(SEARCH_TERM_SEPARATOR);
            });

        this.searchStore
            .getSearchError()
            .pipe(takeUntil(this.destroySubject))
            .subscribe((error) => {
                this.error = error;
            });

        // https://brudetfs.de.eu.sew/tfs/DefaultCollection/IT_PublicALM/_workitems/edit/699514#1230791 ipad should still show mobile icon
        const nameplateBreakpoint = '(min-width: 1025px)';
        this.breakpointObserver
            .observe([mediaQueries.mq3, nameplateBreakpoint])
            .pipe(takeUntil(this.destroySubject))
            .subscribe(({ breakpoints }) => {
                this.isMobile = !breakpoints[mediaQueries.mq3];
                this.showNameplateMobileIcon = !breakpoints[nameplateBreakpoint];
            });
    }

    public onSearch() {
        const searchButton = this.searchButtonRef?.nativeElement;

        startLoading(searchButton);

        this.searchStore.search(this.searchValue);
        this.trackingService.trackEvent(tracking.step.search.startSearchButton);
        this.showHelpExpanded = false;

        this.searchStore
            .getSearchTermAndErrorsAndResult()
            .pipe(
                filter(
                    ({ searchResult, orderNumberSearchResult, searchError }) =>
                        // successfull result
                        this.hasSparePartOrOrderNumberSearchResult(searchResult, orderNumberSearchResult) ||
                        this.hasAnyOtherSearchResult(searchResult) ||
                        // unsuccessfull result
                        this.hasErrorResult(searchResult, orderNumberSearchResult, searchError)
                ),
                map(({ searchTerm }) => searchTerm),
                distinctUntilChanged(arrayEqualsShallow),
                take(1)
            )
            .subscribe(() => endLoading(searchButton));
    }

    public openMultipleInputModal() {
        this.trackingService.trackEvent(tracking.step.search.multipleSearch.open);

        this.modalService.openDialog({
            component: SearchMultipleComponent,
            configuration: {
                type: '',
                headline: '',
                label: '',
                status: '',
                open: true,
                size: 'm',
                closeButton: true,
            },
        });
    }

    public resetSearchError() {
        this.error = undefined;
    }

    public onNameplateSearchResult(result: NamePlateSearchResult) {
        if (result.serialNumber) {
            this.searchValue = result.serialNumber;
            this.searchStore.updateSearchError(null);

            return;
        }

        const content = result.fileSizeExceeded
            ? 'SEARCH.NAMEPLATE.FILE_SIZE_EXCEEDED'
            : result.invalidFileFormat
              ? 'SEARCH.NAMEPLATE.INVALID_FORMAT'
              : 'SEARCH.NAMEPLATE.NOT_FOUND';

        this.searchStore.updateSearchError({ content });
    }

    private hasSparePartOrOrderNumberSearchResult = (
        searchResult: SearchResult,
        orderNumberSearchResult: OrderNumberSearchResult | null | undefined
    ) => !!searchResult || !!orderNumberSearchResult;

    private hasAnyOtherSearchResult = (searchResult: SearchResult) =>
        /* istanbul ignore next */
        !!searchResult &&
        (searchResult.type === SearchResultType.TYPE_DESIGNATION ||
            searchResult.type === SearchResultType.FREE_TEXT ||
            searchResult.type === SearchResultType.SPARE_PARTS_DATA);

    private hasErrorResult = (
        searchResult: SearchResult,
        orderNumberSearchResult: OrderNumberSearchResult | null | undefined,
        searchError: SearchError | null | undefined
    ) =>
        !searchResult &&
        !orderNumberSearchResult &&
        !!searchError &&
        (searchError.orderNumberItemsChecked === undefined || searchError.orderNumberItemsChecked === true);
}
